import type { SharePointSelection } from "./SharePointPicker";

/**
 * A SharePoint data source's selection, as stored in its config: the site and
 * library, then either one folder or a list of files (sync reads the folder
 * first, so a stale folder id would hide a file selection).
 */
const SELECTION_KEYS = [
  "sharepoint_site_url",
  "sharepoint_site_id",
  "sharepoint_site_name",
  "sharepoint_drive_id",
  "sharepoint_drive_name",
  "sharepoint_folder_id",
  "sharepoint_folder_name",
  "sharepoint_folder_path",
  "selected_item_ids",
  "selected_item_names",
] as const;

/** Map a picker selection onto the config the backend expects for a SharePoint source. */
export function sharePointConfig(sel: SharePointSelection): Record<string, unknown> {
  const base: Record<string, unknown> = {
    sharepoint_site_url: sel.site.web_url,
    sharepoint_site_id: sel.site.id,
    sharepoint_site_name: sel.site.display_name,
    sharepoint_drive_id: sel.drive.id,
    sharepoint_drive_name: sel.drive.name,
  };
  if (sel.folder) {
    base.sharepoint_folder_id = sel.folder.item.id;
    base.sharepoint_folder_name = sel.folder.item.name;
    base.sharepoint_folder_path = sel.folder.path;
  } else {
    base.selected_item_ids = sel.items.map((i) => i.id);
    base.selected_item_names = sel.items.map((i) => i.name);
  }
  return base;
}

/**
 * The source's config with a new selection. A config sent on update replaces the stored one, so everything
 * that isn't the selection (the sync schedule, for one) is carried over, and the old selection is dropped.
 */
export function withSharePointSelection(
  config: Record<string, unknown> | undefined,
  sel: SharePointSelection,
): Record<string, unknown> {
  const rest = Object.fromEntries(
    Object.entries(config ?? {}).filter(([key]) => !(SELECTION_KEYS as readonly string[]).includes(key)),
  );
  return { ...rest, ...sharePointConfig(sel) };
}

const text = (value: unknown) => (typeof value === "string" ? value : "");
const texts = (value: unknown) => (Array.isArray(value) ? value.map(text) : []);

/**
 * The selection a SharePoint source's config describes, so "Edit selection" opens on it; null when the config
 * doesn't name a library. Sources created before the site name was stored show the site URL's last segment.
 */
export function sharePointSelectionFromConfig(config: Record<string, unknown> | undefined): SharePointSelection | null {
  const c = config ?? {};
  const siteId = text(c.sharepoint_site_id);
  const driveId = text(c.sharepoint_drive_id);
  if (!siteId || !driveId) return null;
  const siteUrl = text(c.sharepoint_site_url);
  const site = {
    id: siteId,
    web_url: siteUrl,
    display_name: text(c.sharepoint_site_name) || decodeURIComponent(siteUrl.split("/").findLast(Boolean) ?? ""),
  };
  const drive = { id: driveId, name: text(c.sharepoint_drive_name), web_url: "" };
  const folderId = text(c.sharepoint_folder_id);
  if (folderId) {
    const name = text(c.sharepoint_folder_name);
    return {
      site,
      drive,
      items: [],
      folder: {
        item: { id: folderId, name, web_url: "", is_folder: true },
        path: text(c.sharepoint_folder_path) || name,
      },
    };
  }
  const names = texts(c.selected_item_names);
  const items = texts(c.selected_item_ids)
    .filter(Boolean)
    .map((id, i) => ({ id, name: names[i] || id, web_url: "" }));
  return { site, drive, items };
}
